import { Badge } from "@/components/ui/badge";
import type {
  ConversationCompactionEventDTO,
  ConversationDebugDTO,
} from "@shaarp/common";
import { useTranslation } from "@/hooks/useTranslation";
import {
  COMPACTION_KIND_VARIANTS,
  compactionKindHintKey,
  compactionKindLabelKey,
} from "./compaction";
import { DebugLoading } from "./debug-tab-parts";
import { formatDate, fullNumber } from "./format";

type CompactionTabProps = {
  debug: ConversationDebugDTO | undefined;
  isLoading: boolean;
};

/** Every way this conversation's history was cut down, and what replaced it. */
export function CompactionTab({ debug, isLoading }: CompactionTabProps) {
  const { t } = useTranslation();

  if (isLoading) return <DebugLoading />;
  if (!debug) return null;

  if (debug.compactions.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("chat.debug.compaction.none")}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        {t("chat.debug.compaction.count", {
          count: debug.compactions.length,
          inContext: debug.contextMessageCount,
          total: debug.messageCount,
        })}
      </p>
      {debug.compactions.map((event) => (
        <CompactionCard event={event} key={`${event.kind}-${event.messageId}`} />
      ))}
    </div>
  );
}

function CompactionCard({ event }: { event: ConversationCompactionEventDTO }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={COMPACTION_KIND_VARIANTS[event.kind]}>
          {t(compactionKindLabelKey(event.kind))}
        </Badge>
        <span className="ml-auto font-mono text-muted-foreground text-xs">
          {formatDate(event.at)}
        </span>
      </div>
      <p className="text-muted-foreground text-xs">
        {t(compactionKindHintKey(event.kind))}
      </p>
      {event.estimatedTokensUsed !== null &&
        event.contextWindowSize !== null && (
          <p className="font-mono text-muted-foreground text-xs">
            {t("chat.debug.compaction.tokensAtTurn", {
              used: fullNumber(event.estimatedTokensUsed),
              window: fullNumber(event.contextWindowSize),
            })}
          </p>
        )}
      <CompactionSummary summary={event.summary} />
    </div>
  );
}

function CompactionSummary({ summary }: { summary: string | null }) {
  const { t } = useTranslation();

  if (!summary) {
    return (
      <p className="text-muted-foreground text-xs italic">
        {t("chat.debug.compaction.noSummary")}
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      <p className="font-medium text-xs">
        {t("chat.debug.compaction.summaryTitle")}
      </p>
      <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted p-2.5 text-xs">
        {summary}
      </div>
    </div>
  );
}
